import { SearchItem } from '@yiluhub/yilu-amp-types';
import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { paramConverters, yiluTracking } from 'utils';
import { setSrpSession } from 'utils/searchSession';

import routes from 'router/routes';

import { EurailQueryParams } from '../../../utils/query-params';
import { EurailSearchResultsProps } from '../components/EurailSrpResults/types';

export const useEurailSearchResultsProps = (
  eurailSearchResultsPageQueryParams: EurailQueryParams,
) => {
  const [searchResultsCurrent, setSearchResultsCurrent] = useState<SearchItem[]>([]);
  const navigate = useNavigate();
  const searchResults = useRef<SearchItem[]>([]);
  const eurailSearchResultsProps = useMemo(() => {
    const searchParams: EurailQueryParams = {
      fareClass: eurailSearchResultsPageQueryParams.fareClass,
      adult: eurailSearchResultsPageQueryParams.adult,
      youth: eurailSearchResultsPageQueryParams.youth,
      child: eurailSearchResultsPageQueryParams.child,
      senior: eurailSearchResultsPageQueryParams.senior,
      pass: eurailSearchResultsPageQueryParams.pass,
    };

    const isMissingRequiredParameters = Object.keys(searchParams).some((key) => {
      return (searchParams as Record<string, unknown>)[key] === undefined;
    });

    if (isMissingRequiredParameters) return null;

    if (eurailSearchResultsPageQueryParams?.to) {
      searchParams.to = eurailSearchResultsPageQueryParams.to;
    }

    const setEurailSrpSession = () => {
      setSrpSession({
        fareClass: searchParams.fareClass,
        adult: searchParams.adult,
        youth: searchParams.youth,
        child: searchParams.child,
        senior: searchParams.senior,
        pass: searchParams.pass,
      });
    };

    const _props: EurailSearchResultsProps = {
      searchParams: {
        ...(searchParams as EurailSearchResultsProps['searchParams']),
      },
      onSearchResults(results: SearchItem[]) {
        setEurailSrpSession();
        setSearchResultsCurrent(results);
        searchResults.current = results;
        const eventCategory = results.length ? 'eurail-srp' : 'eurail-srp-null';
        const eventLabel = results.length ? 'eurail_srp_loaded' : 'eurail_srp_null_loaded';

        yiluTracking.sendGAEvent({
          event: 'page_loaded',
          category: eventCategory,
          label: eventLabel,
        });
      },
      onSearchResultClick(searchResult: SearchItem) {
        setEurailSrpSession();
        yiluTracking.sendSelectedIndexSearchEvent(
          searchResult.id,
          searchResults.current,
          'eurail_srp',
        );

        const eurailProductDetailsPageQueryParams = {
          searchResultID: searchResult.id,
        };

        navigate({
          pathname: routes.EURAIL_TIP,
          search: paramConverters.getURLSearchQuery(eurailProductDetailsPageQueryParams),
        });
      },
      onError(error: Error) {
        throw error;
      },
    };

    return _props;
  }, [eurailSearchResultsPageQueryParams, navigate]);

  return { eurailSearchResultsProps, searchResultsCurrent };
};
